/* eslint-env jquery */
import React from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import AvatarSelector from '../components/avatar-selector-view';
import {
  meId,
  meData,
  meIsLoading,
  meAvatarLocationUrls,
  meAvatarUrlsRequestGet,
  meAvatarImageSet,
  meNewAvatarImageSource,
  meIsConflicted,
} from 'modules/me-module';
import environment from 'lib/environment';
import DefaultAvatarImage from 'static/images/default.jpg';
import { uiShowToastrMessage } from 'modules/ui-module';

// Safari does not support HTMLCanvasElement.toBlob() at this time, so import a polyfill to handle the conversion.
if (!HTMLCanvasElement.prototype.toBlob) {
  // Source: https://github.com/blueimp/JavaScript-Canvas-to-Blob
  require('lib/3rdparty-es5/canvas-to-blob.min.js'); // eslint-disable-line
}


export class AvatarSelectorContainer extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      personData: Object.assign({}, this.props.personData),
      errors: {},
      isDirty: false,
      modalIsOpen: false,
      isDropZoneDraggedOver: false,
      userImageUrl: '',
      hasDefaultAvatarImage: false,
      toastrType: '',
      toastrMessageId: '',
      dropBoxClass: '',
      imageUploadSelectClass: '',
      imageEditAndSubmitClass: 'hidden',
    };
  }

  componentDidMount() {
    this.props.meAvatarUrlsRequestGet();
    if (!this.props.hasCustomAvatarImage) {
      // @TODO: (PMP-17) replace XMLHttpRequest with Axios XHR request
      const xhr = new XMLHttpRequest();
      xhr.open('HEAD', `${this.props.avatarImageUrl}`, true);
      xhr.send();
      xhr.onreadystatechange = () => {
        if (this.readyState === this.HEADERS_RECEIVED) {
          const xAmzMetaType = xhr.getResponseHeader('x-amz-meta-type');
          if (xAmzMetaType !== 'undefined' && xAmzMetaType === 'default') {
            this.setState({hasDefaultAvatarImage: true});
          }
        }
      };
    }
  }

  openModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState({modalIsOpen: true});
  };

  closeModal = (e) => {
    if (typeof e !== 'undefined') {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState({modalIsOpen: false});
    this.displayImageEditor(false);
  };

  fileDragHover = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const dropBoxClass = (e.type === 'dragover' ? ' hover' : '');
    this.setState({dropBoxClass});
  };

  displayImageEditor = (showImageEditor) => {
    const imageUploadSelectClass = showImageEditor ? 'hidden' : '';
    const imageEditAndSubmitClass = showImageEditor ? '' : 'hidden';
    this.setState({imageUploadSelectClass, imageEditAndSubmitClass});
  };

  fileSelectHandler = (e) => {
    // cancel event and hover styling
    this.fileDragHover(e);

    // fetch FileList object
    const files = e.target.files || e.dataTransfer.files;

    // select the first file as multiple items may be dragged into the drop zone
    let file = null;
    if (files.length) { file = files[0]; }

    const urlReader = new FileReader();

    urlReader.onload = (ev) => {
      this.displayImageEditor(true);
      this.setState({userImageUrl: ev.target.result});
    };

    if (file) {
      urlReader.readAsDataURL(file);
    }
  };

  showImageSelectorControl = (e) => {
    e.stopPropagation();
    e.preventDefault();
    this.displayImageEditor(false);
    this.fileSelectorClick();
    this.setState({userImageUrl: ''});
  };

  fileSelectorClick = () => {
    const fileSelector = document.getElementById('fileSelector');
    if (fileSelector) { fileSelector.click(); }
  };

  handleAjaxResult = (success, isUpload) => {
    const toastrType = success ? 'success' : 'error';
    const toastrMessageId = success ?
      `avatar-selector.${isUpload ? 'avatar-upload-successful' : 'reset-to-default-photo_successful'}` :
      `avatar-selector.${isUpload ? 'avatar-upload-failed' : 'reset-to-default-photo_failed'}`;
    this.props.uiShowToastrMessage(toastrType, toastrMessageId);
  };

  resetToDefaultAvatar = (event) => {
    event.preventDefault();
    event.stopPropagation();
    // @TODO: (PMP-17) replace XmlHttpRequests with Axios XHR requests
    const xhr = new XMLHttpRequest();
    xhr.open('DELETE', `${this.props.avatarDeleteUrl}`, true);
    xhr.send();
    xhr.onreadystatechange = () => {
      if (xhr.readyState === xhr.DONE) {
        if (xhr.status !== 'undefined') {
          if (xhr.status === 204) {
            this.handleAjaxResult(true, false);
            this.props.meAvatarImageSet(DefaultAvatarImage);
            this.setState({hasDefaultAvatarImage: true});
          } else {
            this.handleAjaxResult(false, false);
          }
          this.closeModal();
        }
      }
    };
  };

  processImageAndUpload = (canvasImage) => {
    canvasImage.toBlob((blob) => {
      const contentReader = new FileReader();
      contentReader.onload = (evt) => {
        const imageResult = evt.target.result;
        // @TODO: (PMP-17) replace XmlHttpRequests with Axios XHR requests
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', `${this.props.avatarUpdateUrl}`, true);
        xhr.send(imageResult);
        xhr.onreadystatechange = () => {
          if (xhr.readyState === xhr.DONE) {
            if (xhr.status !== 'undefined') {
              if (xhr.status === 200) {
                const urlCreator = window.URL || window.webkitURL;
                const imageUrl = urlCreator.createObjectURL(blob);
                this.props.meAvatarImageSet(imageUrl);
                this.handleAjaxResult(true, true);
                this.setState({hasDefaultAvatarImage: false});
              } else {
                this.handleAjaxResult(false, true);
              }
              this.closeModal();
            }
          }
        };
      };
      contentReader.readAsArrayBuffer(blob);
    });
  };

  hasCustomAvatarImage = () => this.props.avatarImageUrl.indexOf('blob') > -1 || this.props.avatarImageUrl.indexOf('_medium') > -1;

  render() {
    return (
      <AvatarSelector
        dropBoxClass={this.state.dropBoxClass}
        imageUploadSelectClass={this.state.imageUploadSelectClass}
        imageEditAndSubmitClass={this.state.imageEditAndSubmitClass}
        onAvatarSelectLinkClick={this.openModal}
        modalIsOpen={this.state.modalIsOpen}
        closeModal={this.closeModal}
        processImageAndUpload={this.processImageAndUpload}
        fileDragHover={this.fileDragHover}
        isDropZoneDraggedOver={this.state.isDropZoneDraggedOver}
        fileSelectHandler={this.fileSelectHandler}
        avatarImageUrl={this.props.avatarImageUrl}
        hasCustomAvatarImage={this.hasCustomAvatarImage()}
        userImageUrl={this.state.userImageUrl}
        avatarUpdateUrl={this.props.avatarUpdateUrl}
        resetToDefaultAvatar={this.resetToDefaultAvatar}
        hasDefaultAvatarImage={this.state.hasDefaultAvatarImage}
        fileSelectorClick={this.fileSelectorClick}
        showImageSelectorControl={this.showImageSelectorControl}
        isViewOnly={this.props.isViewOnly}
      />
    );
  }
}

AvatarSelectorContainer.propTypes = {
  personData: PropTypes.object.isRequired,
  avatarImageUrl: PropTypes.string.isRequired,
  avatarUpdateUrl: PropTypes.string.isRequired,
  avatarDeleteUrl: PropTypes.string.isRequired,
  meAvatarUrlsRequestGet: PropTypes.func.isRequired,
  meAvatarImageSet: PropTypes.func.isRequired,
  hasCustomAvatarImage: PropTypes.bool.isRequired,
  uiShowToastrMessage: PropTypes.func.isRequired,
  isViewOnly: PropTypes.bool,
};

export const mapStateToProps = (state) => {
  const avatarBaseImageUrl = environment.get().avatar_image_url;
  let avatarImageUrl = `${avatarBaseImageUrl}${meId(state)}_medium.jpg`;
  let hasCustomAvatarImage = false;
  const newAvatarImage = meNewAvatarImageSource(state);
  if (newAvatarImage) {
    hasCustomAvatarImage = true;
    avatarImageUrl = newAvatarImage;
  }

  const avatarLocationUrls = meAvatarLocationUrls(state);
  let avatarUpdateUrl = '';
  let avatarDeleteUrl = '';
  if (avatarLocationUrls) {
    avatarUpdateUrl = avatarLocationUrls.update || '';
    avatarDeleteUrl = avatarLocationUrls.delete || '';
  }
  return {
    personData: meData(state),
    isLoading: meIsLoading(state),
    isViewOnly: meIsConflicted(state),
    avatarImageUrl,
    avatarUpdateUrl,
    avatarDeleteUrl,
    hasCustomAvatarImage,
  };
};

export const mapDispatchToProps = (dispatch) => ({
  meAvatarUrlsRequestGet: () => dispatch(meAvatarUrlsRequestGet()),
  meAvatarImageSet: (imageResult) => dispatch(meAvatarImageSet(imageResult)),
  uiShowToastrMessage: (toastrType, toastrMessageId) => dispatch(uiShowToastrMessage(toastrType, toastrMessageId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AvatarSelectorContainer);
